/**
 * Shared styles for Select and Combobox components
 *
 * This file contains reusable style utilities for select-like components
 * to ensure consistent styling across Select and Combobox.
 *
 * Uses shared input-styles for consistent base styling.
 */

import {
  inputSizeHeights,
  type InputSize,
} from '@components/ui/styles/input-styles';
import { cn } from '@components/utils/classname';

/**
 * Size variant type for select-like components
 * Matches InputSize for consistency
 */
export type SelectSize = InputSize;

/**
 * Get trigger styles for select-like components
 * Uses shared input styles for consistent appearance with Input component
 *
 * @param size - Size variant
 * @param className - Additional className
 * @returns Combined className string
 */
export function getSelectTriggerStyles(
  size: SelectSize = 'md',
  className?: string,
) {
  // Text size: lg uses body1, others use body2
  const textClass = size === 'lg' ? 'text-body1' : 'text-body2';

  return cn(
    // Base styles - matching input-styles
    'bg-input dark:bg-input/30 border-input-border',
    'data-placeholder:text-muted-foreground [&_svg:not([class*="text-"])]:text-foreground',
    'flex w-fit items-center justify-between gap-2 rounded-md border px-3',
    'whitespace-nowrap shadow-sm transition-colors outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
    // Focus styles - border only (no ring per Figma)
    // data-[state=open] handles when dropdown is open (click)
    'focus:border-ring focus-visible:border-ring data-[state=open]:border-ring',
    // Invalid/Error styles - border only (no ring per Figma)
    'aria-invalid:border-destructive',
    // Size variants - use shared heights
    textClass,
    inputSizeHeights[size],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    (size === 'xxs' || size === 'xs') && '[&_svg:not([class*="size-"])]:size-3',
    size === 'sm' && '[&_svg:not([class*="size-"])]:size-3.5',
    (size === 'md' || size === 'lg') && '[&_svg:not([class*="size-"])]:size-4',
    className,
  );
}

/**
 * Get content/menu styles for select-like components
 *
 * @param options - Configuration options
 * @param options.forRadixSelect - Whether this is for Radix Select (uses dynamic height)
 * @param options.className - Additional className
 * @returns Combined className string
 */
export function getSelectContentStyles(options?: {
  forRadixSelect?: boolean;
  className?: string;
}) {
  const { forRadixSelect = false, className } = options ?? {};

  return cn(
    'bg-menu text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-32 overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
    // Use dynamic height for Radix Select, fixed height for Combobox
    forRadixSelect
      ? 'max-h-(--radix-select-content-available-height) origin-(--radix-select-content-transform-origin)'
      : 'max-h-[300px]',
    className,
  );
}

/**
 * Get item styles for select-like components
 *
 * @param className - Additional className
 * @returns Combined className string
 */
export function getSelectItemStyles(className?: string) {
  return cn(
    // Base styles - use text-primary-foreground as default (not gray)
    'text-primary-foreground text-body2 relative flex w-full cursor-default items-center gap-2 rounded-sm py-2 pr-6 pl-2 outline-hidden select-none',
    // Hover and focus states
    'hover:bg-secondary hover:text-primary-foreground focus:bg-secondary focus:text-primary-foreground',
    // Selected state
    'data-[selected=true]:bg-menu data-[selected=true]:text-primary-foreground',
    // Disabled state
    'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
    // Icon styles
    "[&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    className,
  );
}

/**
 * Get indicator container styles
 *
 * @returns className string for check icon container
 */
export function getIndicatorContainerStyles() {
  return 'absolute top-1/2 right-1 flex size-3.5 -translate-y-1/2 items-center justify-center';
}

/**
 * Get check icon styles
 * The check icon inherits the current text color (currentColor)
 *
 * @returns className string for check icon
 */
export function getCheckIconStyles() {
  return 'size-4 text-current';
}

/**
 * Get label styles for select-like components
 *
 * @param className - Additional className
 * @returns Combined className string
 */
export function getSelectLabelStyles(className?: string) {
  return cn('text-muted-foreground text-label2 px-2 py-1.5', className);
}

/**
 * Get separator styles for select-like components
 *
 * @param className - Additional className
 * @returns Combined className string
 */
export function getSelectSeparatorStyles(className?: string) {
  return cn('bg-border pointer-events-none -mx-1 my-1 h-px', className);
}

/**
 * Get empty state styles for select-like components
 *
 * @param className - Additional className
 * @returns Combined className string
 */
export function getSelectEmptyStyles(className?: string) {
  return cn('text-muted-foreground text-body1 py-6 text-center', className);
}

/**
 * Get chevron icon styles with rotation animation
 *
 * @param options - Configuration options
 * @param options.isOpen - Whether the dropdown/select is open
 * @param options.className - Additional className
 * @returns Combined className string
 */
export function getChevronIconStyles(options?: {
  isOpen?: boolean;
  className?: string;
}) {
  const { isOpen = false, className } = options ?? {};

  return cn(
    'size-4 shrink-0 transition-transform',
    isOpen && 'rotate-180',
    className,
  );
}
